import { useMemo, useState } from 'react';
import {
  Badge,
  ButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import type { InspectJobDiffData, InspectJobDiffRow } from '../api/types';
import { inspectJobDiffColumnConfig } from '../config/inspectJobDiffColumnConfig';
import { formatFaNumber } from '../lib/utils';
import { INDEXER_AG_GRID_FILL_HEIGHT, IndexerAgGrid } from './IndexerAgGrid';
import { UrlActivityModal } from './UrlActivityModal';

type DiffFilter =
  | 'changed'
  | 'became_indexed'
  | 'became_not_indexed'
  | 'error'
  | 'all';

export function InspectJobDiffTable({
  projectId,
  diff,
  compareToLabel,
  fillHeight = false,
}: {
  projectId: string;
  diff: InspectJobDiffData;
  compareToLabel: string;
  fillHeight?: boolean;
}) {
  const [filter, setFilter] = useState<DiffFilter>('changed');
  const [detailUrl, setDetailUrl] = useState<string | null>(null);

  const counts = diff.summary;
  const rows = useMemo(() => {
    return diff.items.filter((row) => {
      if (filter === 'all') return true;
      if (filter === 'changed') return row.changeKind !== 'unchanged';
      if (filter === 'became_indexed') return row.changeKind === 'became_indexed';
      if (filter === 'became_not_indexed') {
        return row.changeKind === 'became_not_indexed';
      }
      if (filter === 'error') {
        return (
          row.changeKind === 'became_error' ||
          row.changeKind === 'resolved_error'
        );
      }
      return true;
    });
  }, [diff.items, filter]);

  const columns = useMemo(
    () =>
      inspectJobDiffColumnConfig({
        onDetail: (url) => setDetailUrl(url),
      }),
    [],
  );

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: InspectJobDiffRow }) => params.data.url,
      animateRows: false,
    }),
    [],
  );

  return (
    <>
      <div className={fillHeight ? 'indexer-fill-grid' : undefined}>
        <div
          className={
            fillHeight
              ? 'indexer-fill-grid-toolbar pb-2'
              : 'px-3 pt-3 pb-2'
          }
        >
          <p className="text-secondary small mb-2">
            مقایسه با جاب قبلی ({compareToLabel}) ·{' '}
            <span className="mono">{diff.compareToJobId.slice(0, 8)}…</span>
          </p>
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Badge bg="primary" pill>
              {formatFaNumber(counts.changed)} تغییر
            </Badge>
            <Badge bg="success" pill>
              +{formatFaNumber(counts.becameIndexed)} ایندکس شد
            </Badge>
            <Badge bg="warning" pill>
              {formatFaNumber(counts.becameNotIndexed)} ایندکس‌نیست شد
            </Badge>
            {counts.becameError > 0 && (
              <Badge bg="danger" pill>
                +{formatFaNumber(counts.becameError)} خطا شد
              </Badge>
            )}
            {counts.resolvedError > 0 && (
              <Badge bg="warning" text="dark" pill>
                {formatFaNumber(counts.resolvedError)} وضعیت مشخص شد
              </Badge>
            )}
          </div>
          <ButtonGroup size="sm">
            {(
              [
                ['changed', `تغییر کرده (${counts.changed})`],
                ['became_indexed', `ایندکس شد (${counts.becameIndexed})`],
                ['became_not_indexed', `ایندکس‌نیست (${counts.becameNotIndexed})`],
                [
                  'error',
                  `خطا/وضعیت (${counts.becameError + counts.resolvedError})`,
                ],
                ['all', `همه (${counts.totalCompared})`],
              ] as const
            ).map(([value, label]) => (
              <ToggleButton
                key={value}
                id={`diff-filter-${value}`}
                type="radio"
                variant="outline-secondary"
                name="diff-filter"
                value={value}
                checked={filter === value}
                onChange={() => setFilter(value)}
              >
                {label}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </div>

        {rows.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-4">
            <p className="mb-0">آدرسی با این فیلتر در مقایسه نیست.</p>
          </div>
        ) : (
          <div className={fillHeight ? 'indexer-fill-grid-body' : undefined}>
            <IndexerAgGrid
              tableName="indexer_inspect_job_diff"
              columns={columns}
              data={rows}
              height={fillHeight ? INDEXER_AG_GRID_FILL_HEIGHT : undefined}
              otherOptions={otherOptions}
            />
          </div>
        )}
      </div>

      {detailUrl && (
        <UrlActivityModal
          projectId={projectId}
          url={detailUrl}
          show
          onHide={() => setDetailUrl(null)}
        />
      )}
    </>
  );
}
